import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortalContext } from '../../contexts/PortalContext';
import PortalLayout from '../../components/portal/PortalLayout';
import ConfirmModal from '../../components/ConfirmModal';
import portalApi from '../../services/portalApi';
import { Loader, ArrowLeft, Download, CreditCard, Phone, Mail, AlertCircle } from 'lucide-react';

const PortalInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, business, isAuthenticated } = usePortalContext();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Get brand color and payment capability
  const brandColor = business?.brandColor || '#3b82f6';
  const canAcceptPayments = business?.stripeConnectEnabled === true;
  const businessPhone = business?.phone;
  const businessEmail = business?.email;
  const businessName = business?.name || 'the business';

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const data = await portalApi.getInvoiceById(id, token);
      setInvoice(data);
    } catch (error) {
      console.error('Error loading invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = () => {
    window.location.href = `/pay/invoice/${invoice.payToken}`;
  };

  const handleDownloadPdf = () => {
    setShowPdfModal(true);
  };

  const handlePrintInvoice = () => {
    setShowPdfModal(false);
    // Small delay to let modal close before printing
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center py-12">
          <Loader size={48} className="animate-spin" style={{ color: brandColor }} />
        </div>
      </PortalLayout>
    );
  }

  if (!invoice) {
    return (
      <PortalLayout>
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">Invoice not found</p>
          <button
            onClick={() => navigate('/portal/invoices')}
            className="inline-flex items-center gap-2 px-4 py-3 text-white font-medium rounded-lg transition-all shadow-lg"
            style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor}dd)` }}
          >
            Back to Invoices
          </button>
        </div>
      </PortalLayout>
    );
  }

  // Payment section component
  const PaymentSection = ({ isMobile = false }) => {
    if (invoice.isPaid) {
      return null;
    }

    if (canAcceptPayments && invoice.payToken) {
      return (
        <button 
          onClick={handlePayInvoice}
          disabled={paymentLoading}
          className={`${isMobile ? 'flex-1' : ''} flex items-center justify-center gap-2 px-4 py-3 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50`}
          style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}
        >
          <CreditCard size={18} />
          {paymentLoading ? 'Processing...' : 'Pay Now'}
        </button>
      );
    } else {
      return (
        <div className={`${isMobile ? 'flex-1' : ''} bg-amber-50 border border-amber-200 rounded-lg p-4`}>
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800">
                Contact {businessName} to pay
              </p>
              <div className="flex flex-wrap gap-3 mt-2">
                {businessPhone && (
                  <a 
                    href={`tel:${businessPhone.replace(/\D/g, '')}`}
                    className="inline-flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-900"
                  >
                    <Phone size={14} />
                    {businessPhone}
                  </a>
                )}
                {businessEmail && (
                  <a 
                    href={`mailto:${businessEmail}?subject=Payment for Invoice ${invoice.invoiceNumber}`}
                    className="inline-flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-900"
                  >
                    <Mail size={14} />
                    Email
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      {/* Print Styles */}
      <style>
        {`
          @media print {
            /* Page settings */
            @page {
              margin: 0.4in;
              size: letter;
            }

            /* Hide portal layout chrome */
            header, nav, footer,
            .print\\:hidden {
              display: none !important;
            }
            
            /* Reset body */
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            
            /* Make sure invoice shows */
            #printable-invoice {
              margin: 0 !important;
              padding: 20px !important;
              box-shadow: none !important;
              border-radius: 0 !important;
              border: none !important;
            }
            
            /* Show print-only elements */
            .hidden.print\\:block {
              display: block !important;
            }
            
            .hidden.print\\:table,
            .hidden.sm\\:table.print\\:table {
              display: table !important;
            }
            
            /* Hide mobile card layout */
            .sm\\:hidden {
              display: none !important;
            }
            
            /* Ensure colors print */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            /* Remove any fixed/sticky positioning */
            .fixed, .sticky {
              display: none !important;
            }
            
            /* Ensure main content isn't constrained */
            main {
              padding: 0 !important;
              max-width: none !important;
            }
            
            /* Remove shadows */
            .shadow-sm, .shadow {
              box-shadow: none !important;
            }
          }
        `}
      </style>

      <PortalLayout>
        <div className="space-y-4 sm:space-y-6">
          {/* Header - Hidden on print */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
            <button
              onClick={() => navigate('/portal/invoices')}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 w-fit"
            >
              <ArrowLeft size={20} />
              <span>Back to Invoices</span>
            </button>
            
            <div className="hidden sm:flex items-center gap-3">
              <PaymentSection />
              <button 
                onClick={handleDownloadPdf}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Download size={18} />
                Download PDF
              </button>
            </div>
          </div>

          {/* Printable Invoice */}
          <div id="printable-invoice" className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Invoice Header */}
            <div 
              className="p-6 sm:p-8"
              style={{ borderBottom: `3px solid ${brandColor}` }}
            >
              {/* Business Logo/Name for Print */}
              <div className="hidden print:block mb-6">
                <h2 className="text-xl font-bold" style={{ color: brandColor }}>
                  {invoice.business.name}
                </h2>
                {invoice.business.phone && (
                  <p className="text-sm text-gray-600">{invoice.business.phone}</p>
                )}
                {invoice.business.email && (
                  <p className="text-sm text-gray-600">{invoice.business.email}</p>
                )}
              </div>

              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">INVOICE</h1>
                  <p className="text-gray-600 mt-1">#{invoice.invoiceNumber}</p>
                </div>
                {invoice.isPaid ? (
                  <span className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium bg-green-100 text-green-700">
                    PAID
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium bg-amber-100 text-amber-700">
                    UNPAID
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">From</h3>
                  <p className="font-semibold text-gray-900">{invoice.business.name}</p>
                  {invoice.business.address && (
                    <>
                      <p className="text-sm text-gray-600">{invoice.business.address}</p>
                      <p className="text-sm text-gray-600">
                        {[invoice.business.city, invoice.business.state, invoice.business.zipCode]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </>
                  )}
                  {invoice.business.email && (
                    <p className="text-sm text-gray-600">{invoice.business.email}</p>
                  )}
                  {invoice.business.phone && (
                    <p className="text-sm text-gray-600">{invoice.business.phone}</p>
                  )}
                </div>

                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Bill To</h3>
                  <p className="font-semibold text-gray-900">{invoice.customer.name}</p>
                  {invoice.customer.address && (
                    <>
                      <p className="text-sm text-gray-600">{invoice.customer.address}</p>
                      <p className="text-sm text-gray-600">
                        {[invoice.customer.city, invoice.customer.state, invoice.customer.zipCode]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </>
                  )}
                  {invoice.customer.email && (
                    <p className="text-sm text-gray-600 mt-1">{invoice.customer.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:gap-8 mt-6 pt-6 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Invoice Date</p>
                  <p className="font-medium text-gray-900 mt-1">{formatDate(invoice.createdAt)}</p>
                </div>
                {invoice.dueDate && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Due Date</p>
                    <p className="font-medium text-gray-900 mt-1">{formatDate(invoice.dueDate)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Line Items */}
            <div className="p-6 sm:p-8">
              {/* Mobile: Card-style line items - Hidden on print */}
              <div className="sm:hidden print:hidden space-y-4">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Services</h3>
                {invoice.lineItems && invoice.lineItems.length > 0 ? (
                  invoice.lineItems.map((item, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-gray-900">{item.serviceName}</p>
                        <p className="font-semibold text-gray-900">${item.total.toFixed(2)}</p>
                      </div>
                      {item.notes && (
                        <p className="text-sm text-gray-500 mt-2">{item.notes}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="font-medium text-gray-900">{invoice.description || 'Service'}</p>
                    <p className="font-semibold text-gray-900 mt-1">${invoice.subtotal.toFixed(2)}</p>
                  </div>
                )}
              </div>

              {/* Desktop/Print: Table-style line items */}
              <table className="hidden sm:table print:table w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="text-center py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="text-right py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                    <th className="text-right py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lineItems && invoice.lineItems.length > 0 ? (
                    invoice.lineItems.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-4">
                          <p className="font-medium text-gray-900">{item.serviceName}</p>
                          {item.notes && (
                            <p className="text-sm text-gray-500 mt-1">{item.notes}</p>
                          )}
                        </td>
                        <td className="py-4 text-center text-gray-900">{item.quantity}</td>
                        <td className="py-4 text-right text-gray-900">${item.unitPrice.toFixed(2)}</td>
                        <td className="py-4 text-right font-medium text-gray-900">
                          ${item.total.toFixed(2)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="py-4 font-medium text-gray-900">{invoice.description || 'Service'}</td>
                      <td className="py-4 text-center text-gray-900">1</td>
                      <td className="py-4 text-right text-gray-900">${invoice.subtotal.toFixed(2)}</td>
                      <td className="py-4 text-right font-medium text-gray-900">${invoice.subtotal.toFixed(2)}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Totals */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-end">
                  <div className="w-full sm:w-64 space-y-2">
                    {(() => {
                      // Calculate line items total to detect complimentary invoices
                      const lineItemsTotal = invoice.serviceValue || invoice.lineItems?.reduce((sum, item) => sum + (item.total || 0), 0) || 0;
                      const isComplimentary = lineItemsTotal > 0 && invoice.total === 0;

                      return (
                        <>
                          {isComplimentary ? (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Service Value</span>
                                <span className="text-gray-900">${lineItemsTotal.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Complimentary Discount</span>
                                <span className="text-green-600">-${lineItemsTotal.toFixed(2)}</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Subtotal</span>
                              <span className="text-gray-900">${invoice.subtotal.toFixed(2)}</span>
                            </div>
                          )}
                          {invoice.taxAmount > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Tax</span>
                              <span className="text-gray-900">${invoice.taxAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="border-t border-gray-200 pt-2 flex justify-between">
                            <span className="font-semibold text-gray-900">Total</span>
                            <span className="font-bold text-xl text-gray-900">
                              ${invoice.total.toFixed(2)}
                            </span>
                          </div>
                          {isComplimentary && (
                            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <p className="text-sm text-green-700 text-center">
                                Complimentary service — no charge
                              </p>
                            </div>
                          )}
                          {!invoice.isPaid && invoice.amountDue > 0 && (
                            <div className="flex justify-between pt-2" style={{ color: brandColor }}>
                              <span className="font-semibold">Amount Due</span>
                              <span className="font-bold text-xl">
                                ${invoice.amountDue.toFixed(2)}
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Notes - Only show on print if present */}
              {invoice.notes && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Notes</h3>
                  <p className="text-sm text-gray-600">{invoice.notes}</p>
                </div>
              )}

              {/* Thank you message - Print only */}
              <div className="hidden print:block mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600">Thank you for your business!</p>
              </div>
            </div>
          </div>

          {/* Mobile: Bottom action buttons - Hidden on print */}
          {!invoice.isPaid && (
            <div className="sm:hidden print:hidden fixed bottom-16 left-0 right-0 p-4 bg-white border-t border-gray-200 space-y-3">
              <PaymentSection isMobile={true} />
              <button 
                onClick={handleDownloadPdf}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg"
              >
                <Download size={18} />
                Download PDF
              </button>
            </div>
          )}

          {invoice.isPaid && (
            <div className="sm:hidden print:hidden fixed bottom-16 left-0 right-0 p-4 bg-white border-t border-gray-200">
              <button 
                onClick={handleDownloadPdf}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg"
              >
                <Download size={18} />
                Download PDF
              </button>
            </div>
          )}
        </div>

        {/* PDF Download Modal */}
        <ConfirmModal
          isOpen={showPdfModal}
          onClose={() => setShowPdfModal(false)}
          onConfirm={handlePrintInvoice}
          title="Download Invoice"
          message="Click below to open the print dialog. Select 'Save as PDF' or 'Microsoft Print to PDF' as your printer to save a PDF copy."
          confirmText="Print / Save PDF"
          cancelText="Cancel"
          variant="info"
        />
      </PortalLayout>
    </>
  );
};

export default PortalInvoiceDetail;